import {css} from '@mui/material';
import React from 'react';

/** One episode's line in a patient's row: the 30px action buttons with the design's 9px above and below. */
export const ALERT_EPISODE_STACK_ROW_HEIGHT = 48;
/** The horizontal padding of a body cell of the big table variant, which the dividers reach across. */
const CELL_PADDING_X = 12;

type Props<T> = {
  items: T[];
  getKey: (item: T, index: number) => React.Key;
  render: (item: T, index: number) => React.ReactNode;
  /** Horizontal placement of each line's content. */
  justify?: 'flex-start' | 'center' | 'flex-end';
};

const stackCss = css`
  display: flex;
  flex-direction: column;
  margin: 0 -${CELL_PADDING_X}px;
`;
const lineCss = css`
  display: flex;
  align-items: center;
  height: ${ALERT_EPISODE_STACK_ROW_HEIGHT}px;
  min-width: 0;
  padding: 0 ${CELL_PADDING_X}px;
  overflow: hidden;
  /* the only lines of a patient's row: between its episodes, across the cell padding so they join up cell to cell */
  & + & {
    border-top: 1px solid rgba(0, 0, 0, 0.07);
  }
`;

/**
 * A patient's episodes stacked in one table cell of the alerts list (PDW-183), one fixed-height line each, so every
 * episode column lines up and the patient and total cells span them all, as the design's merged cells. The table keeps
 * one row per patient, so its banding and paging are per patient.
 */
export function AlertEpisodeStack<T>(props: Props<T>): React.ReactElement {
  const {items, getKey, render, justify = 'flex-start'} = props;
  return (
    <div css={stackCss} data-name={'dl-alert-episode-stack'}>
      {items.map((item, index) => (
        <div css={lineCss} key={getKey(item, index)} style={{justifyContent: justify}} data-name={'dl-alert-episode-stack--line'}>
          {render(item, index)}
        </div>
      ))}
    </div>
  );
}
